import React, { useState } from "react";
import { Layout } from "antd";
import { Link } from "react-router-dom";

import CustomScrollbars from "util/CustomScrollbars";
import languageData from "./languageData";
import { switchLanguage, toggleCollapsedSideNav } from "../../appRedux/actions";
import UserInfo from "../../components/UserInfo";
import Auxiliary from "util/Auxiliary";

import {
  NAV_STYLE_DRAWER,
  NAV_STYLE_FIXED,
  NAV_STYLE_MINI_SIDEBAR,
  TAB_SIZE,
} from "../../constants/ThemeSetting";
import { useDispatch, useSelector } from "react-redux";
import { useGetBreakpoints } from "../../hooks/useGetBreakpoints";
import { MailOutlined, PhoneOutlined } from "@ant-design/icons";
import { useWalletSummary } from "../../hooks/useWalletSummary";
import { isFreightCreditUser } from "../../util/helper";

const { Header } = Layout;

const creditStyles = {
  wrapper: {
    display: "flex",
    alignItems: "center",
  },
  link: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    padding: "9px 14px",
    borderRadius: 10,
    background: "#ffffff",
    // border: "0.5px solid #d3d1c7",
    textDecoration: "none",
    cursor: "pointer",
    transition: "border-color 0.15s, background 0.15s",
  },
  info: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  label: {
    fontSize: 14,
    fontWeight: 600,
    color: "#3B6D11",
    letterSpacing: "0.4px",
    textTransform: "uppercase",
    lineHeight: 1,
  },
  amount: {
    fontSize: 16,
    fontWeight: 600,
    color: "#27500A",
    letterSpacing: "-0.3px",
    lineHeight: 1.3,
  },
};

const CreditRingIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    style={{ flexShrink: 0 }}
  >
    <circle cx="16" cy="16" r="13" fill="#EAF3DE" stroke="#97C459" strokeWidth="1" />
    <circle
      cx="16"
      cy="16"
      r="13"
      fill="none"
      stroke="#3B6D11"
      strokeWidth="2.5"
      strokeDasharray="51 30"
      strokeDashoffset="10"
      strokeLinecap="round"
    />
    <text
      x="16"
      y="20"
      textAnchor="middle"
      fontSize="11"
      fontWeight="500"
      fill="#27500A"
      fontFamily="sans-serif"
    >
      ₹
    </text>
  </svg>
);

const Topbar = () => {
  const { locale, navStyle } = useSelector(({ settings }) => settings);
  const navCollapsed = useSelector(({ common }) => common.navCollapsed);
  const width = useSelector(({ common }) => common.width);
  const authUser = useSelector(({ auth }) => auth?.userData);
  const [searchText, setSearchText] = useState("");
  const dispatch = useDispatch();
  const { isMobile } = useGetBreakpoints();
  const { availableBalance } = useWalletSummary();
  const showFreightBalance = isFreightCreditUser(authUser);
  const mobileHeaderStyles = isMobile
    ? {
        minHeight: "auto",
        alignItems: "center",
        paddingTop: "12px",
        paddingBottom: "12px",
      }
    : {};
  const mobileNotificationsStyles = isMobile
    ? {
        width: "100%",
        justifyContent: "flex-end",
        gap: 8,
        rowGap: 10,
      }
    : {};
  const creditLinkStyles = isMobile
    ? {
        ...creditStyles.link,
        padding: "8px 10px",
        gap: 8,
        maxWidth: "100%",
      }
    : creditStyles.link;
  const creditLabelStyles = isMobile
    ? {
        ...creditStyles.label,
        fontSize: 11,
      }
    : creditStyles.label;
  const creditAmountStyles = isMobile
    ? {
        ...creditStyles.amount,
        fontSize: 13,
      }
    : creditStyles.amount;

  const languageMenu = () => (
    <CustomScrollbars className="gx-popover-lang-scroll">
      <ul className="gx-sub-popover">
        {languageData.map((language) => (
          <li
            className="gx-media gx-pointer"
            key={JSON.stringify(language)}
            onClick={() => dispatch(switchLanguage(language))}
          >
            <i className={`flag flag-24 gx-mr-2 flag-${language.icon}`} />
            <span className="gx-language-text">{language.name}</span>
          </li>
        ))}
      </ul>
    </CustomScrollbars>
  );

  return (
    <Header
      style={{
        minHeight: "112px",
        display: "flex",
        alignItems: "flex-start",
        paddingTop: "24px",
        ...mobileHeaderStyles,
      }}
    >
      {navStyle === NAV_STYLE_DRAWER ||
      ((navStyle === NAV_STYLE_FIXED || navStyle === NAV_STYLE_MINI_SIDEBAR) &&
        width < TAB_SIZE) ? (
        <div className="gx-linebar gx-mr-3">
          <i
            className="gx-icon-btn icon icon-menu"
            onClick={() => {
              dispatch(toggleCollapsedSideNav(!navCollapsed));
            }}
          />
        </div>
      ) : null}

      <Link to="/" className="myexim-logo gx-d-block gx-d-lg-none gx-pointer">
        <img alt="" src={"/assets/images/logo.svg"} />
      </Link>

      <ul
        className="gx-header-notifications gx-ml-auto"
        style={mobileNotificationsStyles}
      >
        {showFreightBalance && (
          <li
            style={{
              ...creditStyles.wrapper,
              marginRight: isMobile ? 0 : 16,
              flexShrink: 1,
              minWidth: 0,
            }}
          >
            <Link to="/my-profile" style={creditLinkStyles}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect x="2" y="7" width="20" height="14" rx="2.5" stroke="#3B6D11" stroke-width="1.5"/>
  <path d="M2 11h20" stroke="#3B6D11" stroke-width="1.5"/>
  <path d="M6 4h12" stroke="#3B6D11" stroke-width="1.5" stroke-linecap="round"/>
  <circle cx="16.5" cy="15.5" r="1.5" fill="#639922"/>
</svg>
              <div style={creditStyles.info}>
                <span style={creditLabelStyles}>Credit Balance</span>
                <span style={creditAmountStyles}>
                  INR {Number(availableBalance || 0).toLocaleString("en-IN")}
                </span>
              </div>
            </Link>
          </li>
        )}

        <Auxiliary>
          <li
            className="gx-user-nav"
            style={
              isMobile
                ? {
                    marginLeft: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                  }
                : undefined
            }
          >
            <UserInfo />
            {isMobile && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 10,
                  marginTop: "8px",
                  width: "100%",
                }}
              >
                <a
                  href="mailto:contactus@myeximbusiness.com"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    fontWeight: 500,
                    fontSize: 14,
                    color: "#999",
                    display: "flex",
                    alignItems: "center",
                    textDecoration: "none",
                    padding: "4px",
                  }}
                >
                  <MailOutlined
                    style={{
                      fontSize: "18px",
                      color: "#1890ff",
                    }}
                  />
                </a>

                <a
                  href="tel:+917358725025"
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    fontWeight: 500,
                    fontSize: 14,
                    color: "#999",
                    display: "flex",
                    alignItems: "center",
                    textDecoration: "none",
                    padding: "4px",
                  }}
                >
                  <PhoneOutlined
                    style={{
                      fontSize: "18px",
                      color: "#52c41a",
                    }}
                  />
                </a>
              </div>
            )}
          </li>
        </Auxiliary>
      </ul>
    </Header>
  );
};

export default Topbar;
